import { z } from 'zod';
import { baseListQuery, uuidSchema } from './common';
import type { TypeWaybillFormCode } from './waybills';

// ── Справочник «Техника»: конкретные ТС (ADR 0007) ──
// Тип обязателен, марка/модель опциональна (в источнике есть машины без марки). Согласованность
// «тип ТС = тип модели» гарантирует составной FK в БД. Госномер нормализуется и уникален среди живых.

export const VEHICLE_STATUSES = ['active', 'inactive', 'maintenance', 'retired'] as const;
export const vehicleStatusSchema = z.enum(VEHICLE_STATUSES);
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

export const vehicleStatusLabels: Record<VehicleStatus, string> = {
  active: 'Активна',
  inactive: 'Неактивна',
  maintenance: 'Обслуживание',
  retired: 'Списана',
};
export const vehicleStatusColors: Record<VehicleStatus, string> = {
  active: 'green',
  inactive: 'default',
  maintenance: 'gold',
  retired: 'red',
};

// ── Принадлежность: собственная техника и аренда (ADR 0018) ──
// Ветки не пересекаются ни одним содержательным реквизитом: у своей машины — марка/модель,
// госномер и ПТС, у аренды — арендодатель, цены и короткий срез-идентификатор.

export const VEHICLE_OWNERSHIPS = ['own', 'rental'] as const;
export const vehicleOwnershipSchema = z.enum(VEHICLE_OWNERSHIPS);
export type VehicleOwnership = (typeof VEHICLE_OWNERSHIPS)[number];

export const vehicleOwnershipLabels: Record<VehicleOwnership, string> = {
  own: 'Собственная',
  rental: 'Аренда',
};
export const vehicleOwnershipColors: Record<VehicleOwnership, string> = {
  own: 'blue',
  rental: 'purple',
};

/** У предложения аренды нет состояний машины: «Обслуживание» и «Списана» к нему не применимы. */
export const RENTAL_STATUSES = ['active', 'inactive'] as const satisfies readonly VehicleStatus[];

export function isRentalStatus(s: VehicleStatus): boolean {
  return (RENTAL_STATUSES as readonly VehicleStatus[]).includes(s);
}

/** Цена в рублях: две цифры после запятой (numeric(12,2) в БД), строго положительная. */
export const vehiclePriceSchema = z.coerce.number().positive().max(9_999_999_99).multipleOf(0.01);

/**
 * Часов в смене: столько же, сколько в сутках, и не меньше часа. Схема общая со ставками
 * назначенной техники (ADR 0027) — «смена» в справочнике и в заявке значит одно и то же.
 */
export const shiftHoursSchema = z.coerce.number().int().min(1).max(24);

// Сортировка доступна во всех столбцах таблицы; ключ поля совпадает с ключом колонки.
export const VEHICLE_SORT_FIELDS = [
  'ownership',
  'registrationNumber',
  'typeName',
  'categoryName',
  'modelName',
  'lessorName',
  'description',
  'pricePerHour',
  'pricePerShift',
  'status',
  'createdAt',
] as const;

export const vehicleListQuerySchema = baseListQuery(VEHICLE_SORT_FIELDS).extend({
  ownership: vehicleOwnershipSchema.optional(),
  vehicleTypeId: uuidSchema.optional(),
  /**
   * Вся техника вида, а не одного типа. Границей замены вид быть перестал (ADR 0064), но окно
   * назначения спрашивает им заказанный вид отдельным запросом: страница списка ограничена 500
   * строками, и парк, в неё не поместившийся, обрезал бы как раз то, что заказывали.
   */
  vehicleKindId: uuidSchema.optional(),
  vehicleCategoryId: uuidSchema.optional(),
  lessorId: uuidSchema.optional(),
  status: vehicleStatusSchema.optional(),
  includeDeleted: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});

/** Общее у веток: классификация, статус, комментарий. Тип обязателен всегда, категория — нет. */
const vehicleCommonFields = {
  vehicleTypeId: uuidSchema,
  vehicleCategoryId: uuidSchema.nullish(),
  status: vehicleStatusSchema.optional().default('active'),
  note: z.string().trim().max(2000).optional().default(''),
};

const createOwnVehicleSchema = z
  .object({
    ownership: z.literal('own'),
    ...vehicleCommonFields,
    vehicleModelId: uuidSchema.nullish(),
    registrationNumber: z.string().trim().max(50).nullish(),
    passportNumber: z.string().trim().max(100).nullish(),
  })
  .strict();

const createRentalVehicleSchema = z
  .object({
    ownership: z.literal('rental'),
    ...vehicleCommonFields,
    status: z.enum(RENTAL_STATUSES).optional().default('active'),
    lessorId: uuidSchema,
    /** Короткий срез вида «Автокран 70 тн»; входит в ключ уникальности предложения. */
    description: z.string().trim().max(120).optional().default(''),
    pricePerHour: vehiclePriceSchema.nullish(),
    pricePerShift: vehiclePriceSchema.nullish(),
    shiftHours: shiftHoursSchema.nullish(),
  })
  .strict()
  .refine((v) => v.pricePerHour != null || v.pricePerShift != null, {
    message: 'Укажите хотя бы одну цену — за час или за смену',
    path: ['pricePerHour'],
  });

// Строгие ветки союза физически отсекают «госномер у аренды» и «цену у своей машины» ещё
// на валидации: чужое поле не пройдёт `.strict()`.
export const createVehicleSchema = z.discriminatedUnion('ownership', [
  createOwnVehicleSchema,
  createRentalVehicleSchema,
]);
export type CreateVehicleInput = z.infer<typeof createVehicleSchema>;
export type CreateOwnVehicleInput = z.infer<typeof createOwnVehicleSchema>;
export type CreateRentalVehicleInput = z.infer<typeof createRentalVehicleSchema>;

// `ownership` в PATCH не принимается: смена принадлежности — другая сущность, а не правка.
// `.partial()` не снимает `.default()`, поэтому поля с дефолтом переобъявлены без него — иначе
// PATCH со сменой одного статуса затирал бы note пустой строкой.
const updateOwnVehicleSchema = z
  .object({
    vehicleTypeId: uuidSchema.optional(),
    vehicleCategoryId: uuidSchema.nullish(),
    vehicleModelId: uuidSchema.nullish(),
    registrationNumber: z.string().trim().max(50).nullish(),
    passportNumber: z.string().trim().max(100).nullish(),
    status: vehicleStatusSchema.optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();

const updateRentalVehicleSchema = z
  .object({
    vehicleTypeId: uuidSchema.optional(),
    vehicleCategoryId: uuidSchema.nullish(),
    lessorId: uuidSchema.optional(),
    description: z.string().trim().max(120).optional(),
    pricePerHour: vehiclePriceSchema.nullish(),
    pricePerShift: vehiclePriceSchema.nullish(),
    shiftHours: shiftHoursSchema.nullish(),
    status: z.enum(RENTAL_STATUSES).optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();

/**
 * Ветку PATCH определяет не тело, а сама запись: клиент не должен сообщать принадлежность,
 * чтобы не было соблазна её «поправить». Маршрут выбирает схему по существующей строке.
 */
export const updateVehicleSchema = z.union([updateOwnVehicleSchema, updateRentalVehicleSchema]);
export type UpdateVehicleInput = z.infer<typeof updateVehicleSchema>;
export const updateVehicleSchemaByOwnership: Record<
  VehicleOwnership,
  typeof updateOwnVehicleSchema | typeof updateRentalVehicleSchema
> = {
  own: updateOwnVehicleSchema,
  rental: updateRentalVehicleSchema,
};
export type UpdateOwnVehicleInput = z.infer<typeof updateOwnVehicleSchema>;
export type UpdateRentalVehicleInput = z.infer<typeof updateRentalVehicleSchema>;

// Поля чужой ветки приходят как null, а не отсутствуют: справочник — один список с одним набором
// колонок, и клиенту не нужно ветвиться при отрисовке строки.
export interface VehicleDto {
  id: string;
  ownership: VehicleOwnership;
  /**
   * Вид ТС типа этой машины. Приезжает строкой справочника, потому что заявку закрывают машиной
   * любого вида (ADR 0064): вид перестал сужать список и стал последним ключом его порядка, а
   * считать порядок форме нечем, если вида в строке нет.
   */
  vehicleKindId: string;
  kindName: string;
  vehicleTypeId: string;
  typeName: string;
  /**
   * The waybill form fixed to the vehicle's type (ADR 0037). It comes with the vehicle row because
   * a request may be closed by a vehicle of another type (ADR 0059): the route's form is set by the
   * unit that will drive, not by the ordered type, and the assignment form must name the change
   * at once. Never empty (ADR 0065), and never `esm2`: a type carries only 4-P or form No. 3, the
   * weekly ESM-2 sheet is issued by the request path (ADR 0128, "known debt").
   */
  waybillFormCode: TypeWaybillFormCode;
  vehicleCategoryId: string | null;
  categoryName: string | null;
  /**
   * Значения ТТХ категории машины (ADR 0016): `{ lift_capacity: 10 }`. Ими считается «крупнее или
   * меньше заказанного» (`compareVehicleSize`); `null` — категории нет либо у типа нет ТТХ.
   */
  categorySpecs: VehicleSpecValues | null;
  vehicleModelId: string | null;
  modelName: string | null;
  registrationNumber: string | null;
  passportNumber: string | null;
  lessorId: string | null;
  lessorName: string | null;
  /**
   * Активен ли арендодатель. У неактивного не может быть активных предложений (ADR 0018 §15):
   * интерфейс по этому полю блокирует включение и объясняет причину, не дожидаясь отказа сервера.
   */
  lessorIsActive: boolean | null;
  /**
   * Выключено каскадом от арендодателя, а не отдельным решением. Такие позиции вернутся сами,
   * когда арендодателя активируют; выключенные вручную — нет (ADR 0018 §14).
   */
  deactivatedWithLessor: boolean;
  description: string;
  pricePerHour: number | null;
  pricePerShift: number | null;
  shiftHours: number | null;
  status: VehicleStatus;
  note: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Ответ правки техники — `PATCH /vehicles/:id`.
 *
 * Обёрткой, а не голым `VehicleDto`, потому что у правки есть **второй исход**: перевод машины в
 * «Списана» и смена её типа на бланк «форма № 3» снимают привязки закреплённых прицепов
 * (план `docs/vehicle-trailers-plan.md`, §4.2.3). Это изменение в базе, которого человек не
 * просил, и §7 обещает прямо: «списание машины или прицепа снимает привязку и говорит, сколько
 * сняло». Промолчи ответ — и диспетчер, списавший тягач, узнает об отцепленном полуприцепе из
 * чужой жалобы.
 *
 * Поля «сколько отцепили» нет и не будет в самой карточке: число снятых привязок — про
 * **операцию**, а не про машину. В `VehicleDto` оно жило бы ложью — читалось бы у каждой строки
 * списка, где никто ничего не снимал. Тем же приёмом разведены исход и карточка у соседей:
 * `UserMutationResult` (`{ user, notified }`) и `HitchTrailerResultDto` (`{ trailer, notice }`).
 *
 * Число, а не фраза (в отличие от `HitchTrailerResultDto.notice`): вытесненного жильца сервер
 * обязан **назвать** — его реквизиты есть только у него, — а здесь говорить нечего сверх счёта,
 * и собрать «отцеплено 2 прицепа» портал может сам. Имя поля то же, что у ответа архивации
 * (`DeleteVehicleResult`): одно событие — одно имя, иначе две двери одного правила разойдутся
 * при первой правке.
 */
export interface UpdateVehicleResult {
  /** Машина после правки — та же карточка, что приезжала голым ответом раньше. */
  vehicle: VehicleDto;
  /** Сколько прицепов сошло с машины этой правкой. `0` — ни один, и говорить не о чем. */
  unhitchedTrailers: number;
}

/**
 * Ответ мягкого удаления техники — `DELETE /vehicles/:id`, кнопка «В архив».
 *
 * Третье событие таблицы плана `docs/vehicle-trailers-plan.md` §4.2.3 (два первых — в
 * `UpdateVehicleResult`): уход машины в архив тоже снимает привязки закреплённых прицепов. Случай
 * опаснее списания: удалённая машина исчезает из списков совсем, и оставленная привязка называла
 * бы тягача, которого в портале уже нет. Обещание §7 у обеих дверей одно — «снимает привязку и
 * говорит, сколько сняло», — и молчать архивации не разрешено ровно так же, как правке.
 *
 * Объявлено типом, а не оставлено лишним полем в `{ ok: true }`: пока формы нет в контрактах,
 * сервер число возвращает, а портал о нём не знает — то самое состояние, в котором обещание §7
 * не выполнено, хотя снятие привязок написано верно. Необъявленный ответ немой: его не читают.
 *
 * Карточки здесь нет и не нужно — удалённую запись списки не показывают, — поэтому число стоит
 * рядом с признаком успеха, а не в обёртке при `VehicleDto`. Но причина та же, что у
 * `UpdateVehicleResult`: сколько отцепили — про **операцию**, а не про машину; в `VehicleDto` это
 * поле читалось бы у каждой строки списка, где никто ничего не снимал.
 *
 * `ok` литералом `true` (как у `RejectUserResult`): отказ эта дверь отдаёт ошибкой HTTP, а не
 * телом с `ok: false`, и придумывать порталу несуществующую ветку разбора незачем.
 */
export interface DeleteVehicleResult {
  /** Всегда `true`: неуспех приезжает ошибкой, а не этим телом. */
  ok: true;
  /** Сколько прицепов сошло с машины её уходом в архив. `0` — ни один, и говорить не о чем. */
  unhitchedTrailers: number;
}

/**
 * Почему предложение аренды нельзя включить, или null — если можно. Текст один и тот же в
 * подсказке у переключателя и в отказе сервера, чтобы человек не гадал, что именно не так.
 */
export function rentalActivationBlockReason(v: {
  ownership: VehicleOwnership;
  lessorIsActive: boolean | null;
  lessorName: string | null;
  deactivatedWithLessor?: boolean;
}): string | null {
  if (v.ownership !== 'rental' || v.lessorIsActive !== false) return null;
  const who = `Арендодатель${v.lessorName ? ` «${v.lessorName}»` : ''} неактивен`;
  // Выключенным вместе с арендодателем возвращаться не нужно вручную — они поднимутся сами.
  return v.deactivatedWithLessor
    ? `${who} — техника включится обратно вместе с ним`
    : `${who} — активируйте его в справочнике контрагентов`;
}

/**
 * Как машина называется в списках и подтверждениях. Ветки различаются тем, чем их различает
 * человек: аренду — коротким срезом предложения, свою машину — госномером. Реквизиты, которых
 * может не быть, перебираются до типа ТС: он есть всегда.
 */
export function vehicleLabel(v: {
  ownership: VehicleOwnership;
  description: string;
  categoryName: string | null;
  typeName: string;
  registrationNumber: string | null;
  modelName: string | null;
}): string {
  if (v.ownership === 'rental') {
    return v.description || v.categoryName || v.typeName;
  }
  return v.registrationNumber || v.modelName || v.categoryName || v.typeName;
}

/** Как строка справочника техники называется в списках и подтверждениях. */
export function vehicleTitle(v: VehicleDto): string {
  return vehicleLabel(v);
}

/**
 * Как машина называется **в строке подбора** — в фильтрах списков и выпадающих списках выбора
 * (ADR 0098). От `vehicleLabel` отличается вторым реквизитом: там, где нужно коротко назвать уже
 * выбранную машину, хватает одного госномера, а выбирают её двумя приметами сразу — «А123БВ797 —
 * КамАЗ 65115». Госномер помнят не для всякой машины, марку — не для всякого госномера, и список
 * из одних номеров заставляет сверяться со справочником в соседней вкладке.
 *
 * Пара та же, какой техника представлена в справочнике: у своей машины — «Госномер» и
 * «Марка/модель», у аренды — «Описание» и «Арендодатель» (у неё госномера нет вовсе, ADR 0018).
 * Отсутствующий реквизит не оставляет пустой половины и не рисует прочерк: строка сокращается до
 * того, что есть, а если нет ни одного — до `vehicleLabel`, где следом идут категория и тип.
 *
 * Порядок реквизитов — он же порядок ввода при поиске: подстрочный поиск по этой строке находит
 * машину и по началу госномера, и по марке.
 */
export function vehicleOptionLabel(v: {
  ownership: VehicleOwnership;
  description: string;
  categoryName: string | null;
  typeName: string;
  registrationNumber: string | null;
  modelName: string | null;
  lessorName?: string | null;
}): string {
  const parts =
    v.ownership === 'rental'
      ? [v.description || null, v.lessorName ?? null]
      : [v.registrationNumber, v.modelName];
  const shown = parts.filter((p): p is string => !!p && p.trim() !== '');
  return shown.length > 0 ? shown.join(' — ') : vehicleLabel(v);
}

// ── Registration plate key (ADR 0138, owner decision S-6) ──

/**
 * The Russian plate alphabet is АВЕКМНОРСТУХ, and every letter of it has a Latin look-alike, so
 * the replacement is unambiguous. The pairs are the two strings `vehicle_reg_normalize` passes to
 * `translate` (migration 0015).
 */
const PLATE_LOOKALIKES: Readonly<Record<string, string>> = {
  А: 'A',
  В: 'B',
  Е: 'E',
  К: 'K',
  М: 'M',
  Н: 'H',
  О: 'O',
  Р: 'P',
  С: 'C',
  Т: 'T',
  У: 'Y',
  Х: 'X',
};

/**
 * The comparison key of a registration plate: separators dropped, upper case, Cyrillic look-alikes
 * replaced with Latin letters. «В 094 ЕТ 77», «в094ет77» and «B094ET77» give one key, B094ET77, so
 * one vehicle typed in two keyboard layouts is one vehicle.
 *
 * **The database function is the authority, this is its pinned copy.** `vehicle_reg_normalize`
 * (migration 0015) backs the generated `registration_number_normalized` columns and the unique
 * indexes of vehicles and trailers, so uniqueness and lookup are decided by Postgres. This copy
 * serves the places that compare plates without a query: the directory exchange, the fuel-norms
 * file, the trailer picker of the route form. `vehicle-reg-normalize-parity.db.test.ts` runs both
 * over the same inputs, so a change on either side alone fails a test instead of drifting: a
 * second, weaker rule in the picker is exactly how a trailer typed in Cyrillic went unrecognised
 * when the registry held it in Latin letters.
 *
 * **The empty key.** For an input with nothing left after the separators the database returns
 * `NULL` (and for `NULL` itself, the function being STRICT); this returns `''`. Both mean "no
 * key", and `''` must be treated the way SQL treats `NULL`: it matches nothing, not even another
 * empty key. Every caller does so — the exchange engine skips `''` keys, the fuel-norms index is
 * built from non-NULL columns only, the picker recognises nothing in an empty box. Returning
 * `null` would mirror the database more literally and buy nothing but a null check in each caller.
 *
 * The key is for comparison only: forms and the registry keep the plate as it was typed.
 */
export function normalizeRegistration(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/[^0-9A-Za-zА-Яа-яЁё]+/gu, '')
    .toUpperCase()
    .replace(/[АВЕКМНОРСТУХ]/gu, (c) => PLATE_LOOKALIKES[c] ?? c);
}

// ── Замена заказанной техники: вид, тип, категория и «крупнее или меньше» (ADR 0045, 0059, 0063) ──
//
// Заявка заказывает позицию классификатора — тип ТС и, если у типа есть ТТХ, его категорию
// (ADR 0028). Исполняет диспетчер тем, что есть в парке: категория перестала быть фильтром в
// ADR 0045, тип — в ADR 0059, вид — в ADR 0064. Границы у замены больше нет вовсе: заказанная
// позиция задаёт порядок списка, а не его состав.
//
// Причина — качество данных, а не убеждение, что вид неважен (ADR 0064): классификатор заполнен
// неровно, заявку заводит один человек, а парк ведёт другой, и запрет по неверной строке
// справочника прячет машину, которой работу как раз и делают. Портал обязан назвать обе стороны
// расхождения и сказать, в какую сторону разошлось, но ничего не запрещает. Правило живёт здесь,
// а не на сервере: одна формулировка на строку списка, предупреждение и тесты — расходиться нечему.

/** Значения ТТХ категории (ADR 0016): код характеристики → число, `{ lift_capacity: 25 }`. */
export type VehicleSpecValues = Readonly<Record<string, number>>;

/**
 * Как техника соотносится с заказанной по **общим** ТТХ.
 *
 * `same` — общие характеристики равны, `bigger` / `smaller` — все не меньше (не больше) и хотя бы
 * одна строго больше (меньше), `mixed` — часть больше, часть меньше (у манипулятора г/п машины
 * выше, а г/п стрелы ниже). `unknown` — сравнивать не с чем: категория не проставлена (в
 * справочнике она необязательна) либо у типов нет ни одной общей характеристики.
 */
export type VehicleSizeRelation = 'same' | 'bigger' | 'smaller' | 'mixed' | 'unknown';

/**
 * Сравнение по ТТХ, а не по названию типа: «крупнее» — измеримая величина, и выдумывать её порядок
 * порталу нечем. Справочник ТТХ глобальный именно ради такого кросс-типового сравнения (ADR 0016
 * §1): грузоподъёмность у бортового и у малотоннажного — одна и та же характеристика.
 *
 * Где общих характеристик нет (самосвал меряется объёмом кузова, а бортовой — грузоподъёмностью;
 * у тягачей и легковых ТТХ не заведены вовсе), ответ честный — `unknown`: это состояние
 * справочника, а не повод угадать.
 */
export function compareVehicleSize(
  ordered: VehicleSpecValues | null | undefined,
  actual: VehicleSpecValues | null | undefined,
): VehicleSizeRelation {
  if (!ordered || !actual) return 'unknown';
  let bigger = false;
  let smaller = false;
  let common = 0;
  for (const [code, orderedValue] of Object.entries(ordered)) {
    const actualValue = actual[code];
    if (actualValue === undefined) continue;
    common += 1;
    if (actualValue > orderedValue) bigger = true;
    if (actualValue < orderedValue) smaller = true;
  }
  if (common === 0) return 'unknown';
  if (bigger && smaller) return 'mixed';
  if (bigger) return 'bigger';
  if (smaller) return 'smaller';
  return 'same';
}

/** Чем выбранная машина разошлась с заказом. `ok` — не разошлась ничем. */
export interface VehicleSubstitution {
  /**
   * Вид ТС другой: заказывали спецтехнику, а берут грузовик. Самое крупное расхождение, какое
   * бывает, — и с ADR 0064 оно тоже не запрет, а предупреждение: классификатор заполнен неровно,
   * и «другой вид» нередко означает не другую работу, а другую строку справочника.
   */
  kindMismatch: boolean;
  /** Тип машины другой. При `kindMismatch` он другой всегда: типы принадлежат видам. */
  typeMismatch: boolean;
  /** Категория другая. У машины другого типа категории всегда разные — поле про свой тип. */
  categoryMismatch: boolean;
  /**
   * Заказана категория, а у машины её нет. Не расхождение (ADR 0045 §6): «не разнесли» — не то же
   * самое, что «не подходит», но и сравнить в этом случае нечего.
   */
  categoryMissing: boolean;
  relation: VehicleSizeRelation;
}

/** Заказанная позиция и то, чем её закрывают, — обе стороны сравнения. */
export interface VehicleClassificationPosition {
  /** Вид ТС: он больше не граница замены (ADR 0064), но остаётся первым, чем позиции различаются. */
  vehicleKindId: string;
  vehicleTypeId: string;
  vehicleCategoryId: string | null;
  categorySpecs: VehicleSpecValues | null;
}

export function vehicleSubstitutionOf(
  ordered: VehicleClassificationPosition,
  actual: VehicleClassificationPosition,
): VehicleSubstitution {
  return {
    kindMismatch: ordered.vehicleKindId !== actual.vehicleKindId,
    typeMismatch: ordered.vehicleTypeId !== actual.vehicleTypeId,
    categoryMismatch:
      !!ordered.vehicleCategoryId &&
      !!actual.vehicleCategoryId &&
      ordered.vehicleCategoryId !== actual.vehicleCategoryId,
    categoryMissing: !!ordered.vehicleCategoryId && !actual.vehicleCategoryId,
    relation: compareVehicleSize(ordered.categorySpecs, actual.categorySpecs),
  };
}

/** Разошлась ли машина с заказом хоть чем-нибудь — коротко, для условий показа. */
export function isVehicleSubstitution(s: VehicleSubstitution): boolean {
  return s.kindMismatch || s.typeMismatch || s.categoryMismatch;
}

/**
 * Короткая пометка в строке выбора — техники, рейса, чего угодно, где строку читают глазами.
 * Направление называется здесь же: «другой тип» без «крупнее» заставляет открывать справочник.
 *
 * У чужого вида направления нет и быть не может: «крупнее» считается по общим ТТХ, а у самосвала
 * с автокраном их не бывает. Пометка называет само расхождение — оно и есть главное, что о такой
 * строке нужно знать.
 */
export function vehicleSubstitutionHint(s: VehicleSubstitution): string | null {
  const size = VEHICLE_SIZE_HINTS[s.relation];
  if (s.kindMismatch) return 'другой вид техники';
  if (s.typeMismatch) return size ? `другой тип, ${size}` : 'другой тип';
  if (s.categoryMismatch) return size ?? 'другая категория';
  if (s.categoryMissing) return 'категория не указана';
  return null;
}

const VEHICLE_SIZE_HINTS: Record<VehicleSizeRelation, string | null> = {
  bigger: 'крупнее',
  smaller: 'меньше заказанного',
  mixed: 'ТТХ расходятся',
  same: null,
  unknown: null,
};

/**
 * Развёрнутое предупреждение там, где машину выбирают: пометки в строке мало — её читают при
 * выборе и забывают, а предупреждение под полем видно до самого нажатия «Взять в работу»
 * (ADR 0045 §4). Названы обе стороны: что заказывали и что берут.
 *
 * Уровень отвечает на «есть ли риск»: техника меньше заказанной или расходится по ТТХ — жёлтое
 * предупреждение (груз может не поместиться), крупнее или «сравнить нечем» — нейтральная справка.
 * Подтверждения не требуется ни там, ни там (ADR 0045 §5): назначение именное, а чекбокс
 * превращается в кнопку, которую жмут не читая.
 */
export function vehicleSubstitutionWarning(input: {
  substitution: VehicleSubstitution;
  /** «Автокраны, г/п 130 т» — заказанная позиция целиком (`vehicleClassificationLabel`). */
  orderedLabel: string;
  actualTypeName: string;
  actualCategoryName: string | null;
}): { level: 'info' | 'warning'; text: string } | null {
  const s = input.substitution;
  if (!isVehicleSubstitution(s)) return null;
  // Наименование категории уже содержит тип («Автокраны, г/п 25 т», ADR 0016 §11) — тип рядом с
  // ней не повторяется, тем же правилом, что и у заказанной стороны (`vehicleClassificationLabel`).
  const actualLabel = input.actualCategoryName || input.actualTypeName;
  // Чужой вид — всегда жёлтое: ТТХ у него сравнивать не с чем, а расхождение самое крупное из
  // возможных. Заявку он не отменяет (ADR 0064), но и нейтральной справкой быть не может.
  if (s.kindMismatch) {
    return {
      level: 'warning',
      text:
        `Заказано «${input.orderedLabel}», а выбрана «${actualLabel}» — это техника другого вида. ` +
        'Заявка возьмётся в работу как есть — проверьте, что работу выполнят именно этой машиной.',
    };
  }
  return {
    level: s.relation === 'smaller' || s.relation === 'mixed' ? 'warning' : 'info',
    text: `Заказано «${input.orderedLabel}», а выбрана «${actualLabel}». ${VEHICLE_SIZE_WARNINGS[s.relation]}`,
  };
}

const VEHICLE_SIZE_WARNINGS: Record<VehicleSizeRelation, string> = {
  bigger: 'Техника крупнее заказанной — заявка возьмётся в работу как есть.',
  smaller: 'Техника меньше заказанной — проверьте, что работа выполнима и груз поместится.',
  mixed: 'Характеристики расходятся с заказанными — проверьте, что техника подходит.',
  same: 'Характеристики те же — заявка возьмётся в работу как есть.',
  unknown: 'Сравнить характеристики не с чем — проверьте, что техника подходит.',
};

/**
 * Куда строка попадёт в списке: заказанный тип, крупнее, меньше, «прочее вида» или чужой вид.
 * Группы задают и порядок (`vehicleSubstitutionRank`) — им же упорядочена подсказка рейсов:
 * сначала соответствие, потом близкое к нему, и только в конце далёкое.
 *
 * Свой тип — одна группа независимо от категории: внутри типа расхождение помечается в строке
 * (ADR 0045), и дробить парк на четыре группы там, где привыкли видеть один список, незачем.
 *
 * Чужой вид — последняя группа, и это единственное, что от снятой границы осталось (ADR 0064):
 * список от неё не сужается, но и предлагать автокран под заявку на самосвал раньше самосвалов
 * нельзя. Порядок групп и есть та «релевантность», ради которой границу можно было снять.
 */
export type VehicleSubstitutionGroup = 'ordered' | 'bigger' | 'other' | 'smaller' | 'kind';

export function vehicleSubstitutionGroup(s: VehicleSubstitution): VehicleSubstitutionGroup {
  if (s.kindMismatch) return 'kind';
  if (!s.typeMismatch) return 'ordered';
  if (s.relation === 'bigger') return 'bigger';
  if (s.relation === 'smaller' || s.relation === 'mixed') return 'smaller';
  return 'other';
}

export const vehicleSubstitutionGroupLabels: Record<VehicleSubstitutionGroup, string> = {
  ordered: 'Заказанный тип',
  bigger: 'Крупнее заказанного',
  other: 'Другие типы вида',
  smaller: 'Меньше заказанного',
  kind: 'Другой вид техники',
};

const VEHICLE_SUBSTITUTION_ORDER: readonly VehicleSubstitutionGroup[] = [
  'ordered',
  'bigger',
  'other',
  'smaller',
  'kind',
];

/** Порядок группы в списке: 0 — заказанный тип, дальше по убыванию пригодности. */
export function vehicleSubstitutionRank(s: VehicleSubstitution): number {
  return VEHICLE_SUBSTITUTION_ORDER.indexOf(vehicleSubstitutionGroup(s));
}

// ── Марки/модели: read-only список для выбора в форме техники (не отдельный справочник) ──
export const VEHICLE_MODEL_SORT_FIELDS = ['name', 'createdAt'] as const;

export const vehicleModelListQuerySchema = baseListQuery(VEHICLE_MODEL_SORT_FIELDS).extend({
  vehicleTypeId: uuidSchema.optional(),
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});

export interface VehicleModelDto {
  id: string;
  vehicleTypeId: string;
  name: string;
  manufacturerName: string;
  isActive: boolean;
}
